// eslint-disable-next-line unicode-bom
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';

import { syncMetaFlows, getMetaFlows } from '../../services/metaFlows';

function safeJsonParseArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parsePrefillKeys(input) {
  if (!input) return [];
  return input
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function WspFlowNodeFields({ localNodeData, updateNodeData, accountList }) {
  const [metaFlows, setMetaFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [apiError, setApiError] = useState('');

  const accountId = localNodeData.accountId || '';

  const refreshFlows = useCallback(async () => {
    if (!accountId) return;
    setApiError('');
    setLoading(true);
    try {
      const list = await getMetaFlows(accountId);
      setMetaFlows(Array.isArray(list) ? list : []);
    } catch {
      setApiError('No se pudo cargar la lista de WhatsApp Flows.');
      setMetaFlows([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    refreshFlows();
  }, [refreshFlows]);

  const handleSync = useCallback(async () => {
    if (!accountId) {
      setApiError('Selecciona un AccountId antes de sincronizar.');
      return;
    }
    setApiError('');
    setSyncing(true);
    try {
      await syncMetaFlows(accountId);
      await refreshFlows();
    } catch {
      setApiError('No se pudo sincronizar desde Meta.');
    } finally {
      setSyncing(false);
    }
  }, [accountId, refreshFlows]);

  const normalizedMetaFlows = useMemo(() => {
    const list = Array.isArray(metaFlows) ? metaFlows : [];

    return list
      .map((flow) => {
        const flowId = flow?.FlowId ?? flow?.flowId ?? flow?.id;
        const name = flow?.Name ?? flow?.name ?? '';
        const status = flow?.Status ?? flow?.status ?? '';
        const validationErrors = flow?.ValidationErrors ?? flow?.validationErrors;
        return { ...flow, FlowId: flowId, Name: name, Status: status, ValidationErrors: validationErrors };
      })
      .filter((flow) => !!flow.FlowId);
  }, [metaFlows]);

  const selectedFlow = useMemo(() => {
    const id = localNodeData.whatsappFlowId;
    if (!id) return null;
    return normalizedMetaFlows.find((f) => f.FlowId === id) || null;
  }, [localNodeData.whatsappFlowId, normalizedMetaFlows]);

  const validationErrors = useMemo(() => {
    if (!selectedFlow) return [];
    return safeJsonParseArray(selectedFlow.ValidationErrors);
  }, [selectedFlow]);

  const prefillKeysValue = useMemo(() => {
    const keys = Array.isArray(localNodeData.whatsappFlowPrefillKeys)
      ? localNodeData.whatsappFlowPrefillKeys
      : [];
    return keys.join(',');
  }, [localNodeData.whatsappFlowPrefillKeys]);

  const missingFlowId = !localNodeData.whatsappFlowId;

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        WhatsApp Flow
      </Typography>

      {apiError ? <Alert severity="error">{apiError}</Alert> : null}

      <FormControl fullWidth size="small">
        <InputLabel id="wspflow-account-label">Account</InputLabel>
        <Select
          labelId="wspflow-account-label"
          label="Account"
          value={accountId}
          onChange={(e) => {
            updateNodeData('accountId', e.target.value);
            // reset dependientes
            updateNodeData('whatsappFlowId', '');
            updateNodeData('whatsappFlowName', '');
          }}
        >
          {(accountList || []).map((acc) => (
            <MenuItem key={acc.id} value={acc.id}>
              {acc.number || acc.name || acc.id}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
          onClick={handleSync}
          disabled={!accountId || syncing}
        >
          Sincronizar desde Meta
        </Button>

        {loading ? (
          <Typography variant="caption" color="text.secondary">
            Cargando...
          </Typography>
        ) : null}
      </Box>

      <FormControl fullWidth size="small" error={missingFlowId}>
        <InputLabel id="wspflow-flow-label">WhatsApp Flow</InputLabel>
        <Select
          labelId="wspflow-flow-label"
          label="WhatsApp Flow"
          value={localNodeData.whatsappFlowId || ''}
          onChange={(e) => {
            const id = e.target.value;
            updateNodeData('whatsappFlowId', id);

            // Sólo para UI (subtítulo del nodo). Backend ignora whatsappFlowName.
            const flow = normalizedMetaFlows.find((f) => f.FlowId === id);
            updateNodeData('whatsappFlowName', flow?.Name || id || '');
          }}
          disabled={!accountId}
        >
          {normalizedMetaFlows.map((flow) => (
            <MenuItem key={flow.FlowId} value={flow.FlowId}>
              {(flow.Name || flow.FlowId) + (flow.Status ? ` (${flow.Status})` : '')}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {missingFlowId ? <Alert severity="warning">WhatsappFlowId es requerido.</Alert> : null}

      {validationErrors.length > 0 ? (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            ValidationErrors
          </Typography>
          {validationErrors.map((err, idx) => (
            <Typography key={idx} variant="caption" display="block">
              • {typeof err === 'string' ? err : JSON.stringify(err)}
            </Typography>
          ))}
        </Alert>
      ) : null}

      <TextField
        size="small"
        label="CTA"
        value={localNodeData.whatsappFlowCta || ''}
        onChange={(e) => updateNodeData('whatsappFlowCta', e.target.value)}
      />

      <TextField
        size="small"
        label="Start screen"
        value={localNodeData.whatsappFlowStartScreen || ''}
        onChange={(e) => updateNodeData('whatsappFlowStartScreen', e.target.value)}
      />

      <TextField
        size="small"
        label="Body text"
        value={localNodeData.bodyText || ''}
        onChange={(e) => updateNodeData('bodyText', e.target.value)}
        multiline
        minRows={3}
      />

      <TextField
        size="small"
        label="Footer text"
        value={localNodeData.footerText || ''}
        onChange={(e) => updateNodeData('footerText', e.target.value)}
        multiline
        minRows={2}
      />

      <TextField
        size="small"
        label="Prefill keys (coma separadas)"
        placeholder="nombre,rut,email"
        value={prefillKeysValue}
        onChange={(e) => updateNodeData('whatsappFlowPrefillKeys', parsePrefillKeys(e.target.value))}
        helperText="Se guarda como string[] en whatsappFlowPrefillKeys"
      />

      {Array.isArray(localNodeData.whatsappFlowPrefillKeys) && localNodeData.whatsappFlowPrefillKeys.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {localNodeData.whatsappFlowPrefillKeys.map((k) => (
            <Chip
              key={k}
              size="small"
              label={k}
              onDelete={() => {
                const next = (localNodeData.whatsappFlowPrefillKeys || []).filter((x) => x !== k);
                updateNodeData('whatsappFlowPrefillKeys', next);
              }}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
